import Database, { type QueryResult } from "@tauri-apps/plugin-sql";
import { type Sql } from "sql-template-tag";

import { env } from "../env";
import { paths } from "../paths";
import { store } from "../store";

export type SQLiteQueryOptions = {
	databasePath?: string;
};

export type SQLiteService = {
	query: <T>(query: Sql, options?: SQLiteQueryOptions) => Promise<T>;
	queryRaw: <T>(
		statement: string,
		values?: unknown[],
		options?: SQLiteQueryOptions
	) => Promise<T>;
	execute: (query: Sql, options?: SQLiteQueryOptions) => Promise<QueryResult | undefined>;
	executeRaw: (
		statement: string,
		values?: unknown[],
		options?: SQLiteQueryOptions
	) => Promise<QueryResult | undefined>;
};

type CachedDatabase = {
	database: Database;
	activeOperations: number;
	lastUsedAt: number;
	pinned: boolean;
};

const MAX_CACHED_UNPINNED_DATABASES = 8;

// Module-level database instance cache
let dbInstances = new Map<string, CachedDatabase>();

export const createTauriSQLiteService = (): SQLiteService => {
	const resolveDatabasePath = async (options?: SQLiteQueryOptions): Promise<string> => {
		if (options?.databasePath) {
			return options.databasePath;
		}

		const pathsApi = paths();
		const momentDbPathAtom = pathsApi.getMomentDbPath();
		let dbPath: string;
		try {
			dbPath = await store.get(momentDbPathAtom);
		} catch {
			throw new Error("Database path not found");
		}

		return dbPath;
	};

	const closeCachedDatabase = async (databasePath: string, cached: CachedDatabase) => {
		const current = dbInstances.get(databasePath);
		if (!current || current !== cached || current.activeOperations > 0) {
			return;
		}

		dbInstances.delete(databasePath);
		await cached.database.close();
	};

	const evictIdleDatabases = async () => {
		const unpinnedEntries = [...dbInstances.entries()].filter(([, cached]) => !cached.pinned);

		if (unpinnedEntries.length <= MAX_CACHED_UNPINNED_DATABASES) {
			return;
		}

		const excessCount = unpinnedEntries.length - MAX_CACHED_UNPINNED_DATABASES;
		const evictableEntries = unpinnedEntries
			.filter(([, cached]) => cached.activeOperations === 0)
			.sort((left, right) => left[1].lastUsedAt - right[1].lastUsedAt)
			.slice(0, excessCount);

		await Promise.all(
			evictableEntries.map(async ([databasePath, cached]) => {
				await closeCachedDatabase(databasePath, cached);
			})
		);
	};

	const acquireDb = async (options?: SQLiteQueryOptions): Promise<CachedDatabase> => {
		const dbPath = await resolveDatabasePath(options);
		const pinned = options?.databasePath === undefined;
		const cachedDb = dbInstances.get(dbPath);
		if (cachedDb) {
			cachedDb.activeOperations += 1;
			cachedDb.lastUsedAt = Date.now();
			cachedDb.pinned ||= pinned;
			return cachedDb;
		}

		const loadedDb = await Database.load(`sqlite:${dbPath}`);
		if (!loadedDb) {
			throw new Error("Failed to load database");
		}

		const cachedDatabase: CachedDatabase = {
			database: loadedDb,
			activeOperations: 1,
			lastUsedAt: Date.now(),
			pinned,
		};

		dbInstances.set(dbPath, cachedDatabase);
		void evictIdleDatabases();

		return cachedDatabase;
	};

	const releaseDb = (cachedDatabase: CachedDatabase) => {
		cachedDatabase.activeOperations = Math.max(0, cachedDatabase.activeOperations - 1);
		cachedDatabase.lastUsedAt = Date.now();
		void evictIdleDatabases();
	};

	const withDb = async <T>(
		options: SQLiteQueryOptions | undefined,
		callback: (database: Database) => Promise<T>
	): Promise<T> => {
		const cachedDatabase = await acquireDb(options);

		try {
			return await callback(cachedDatabase.database);
		} finally {
			releaseDb(cachedDatabase);
		}
	};

	return {
		async query<T>(query: Sql, options?: SQLiteQueryOptions) {
			return withDb(options, (database) => database.select<T>(query.sql, query.values));
		},

		async queryRaw<T>(statement: string, values: unknown[] = [], options?: SQLiteQueryOptions) {
			return withDb(options, (database) => database.select<T>(statement, values));
		},

		async execute(query: Sql, options?: SQLiteQueryOptions) {
			return withDb(options, (database) => database.execute(query.sql, query.values));
		},

		async executeRaw(statement: string, values: unknown[] = [], options?: SQLiteQueryOptions) {
			return withDb(options, (database) => database.execute(statement, values));
		},
	};
};

export const createBrowserSQLiteService = (): SQLiteService => {
	// Maybe this will eventually use IndexedDB or something
	return {
		query<T>() {
			return Promise.resolve<T>(undefined as T);
		},

		queryRaw<T>() {
			return Promise.resolve<T>(undefined as T);
		},

		execute() {
			return Promise.resolve<QueryResult | undefined>(undefined);
		},

		executeRaw() {
			return Promise.resolve<QueryResult | undefined>(undefined);
		},
	};
};

// Singleton instance cache
let sqliteInstance: SQLiteService | undefined;

// Main getter with lazy initialization
export const getSQLite = (): SQLiteService | undefined => {
	if (sqliteInstance !== undefined) {
		return sqliteInstance;
	}

	if (!env.isSsr && env.isDesktopApp) {
		sqliteInstance = createTauriSQLiteService();
	} else {
		sqliteInstance = undefined;
	}

	return sqliteInstance;
};

// Reset for testing
export const resetSQLite = (): void => {
	const databasesToClose = [...dbInstances.values()];
	sqliteInstance = undefined;
	dbInstances = new Map<string, CachedDatabase>();

	for (const cachedDatabase of databasesToClose) {
		void cachedDatabase.database.close().catch(() => undefined);
	}
};
